import { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Text,
  Flex,
  Stack,
  Badge,
  Group,
  Divider,
  Collapse,
  SimpleGrid,
  RingProgress,
  Tooltip,
  Center,
  Loader,
} from "@mantine/core";
import {
  IconCheck,
  IconMessageCircle,
  IconAlertTriangle,
  IconQuestionMark,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import type {
  Questionnaire,
  Question,
  Answer,
  ScaleAnswer,
  PolicyAnswer,
  MultiSelectAnswer,
  FullGradingResult,
  QuestionGradingResult,
  GradeCategory,
} from "@/utils/gradingEngine.types";
import { getGradeDescription } from "@/data/questionnaireGradeDescriptions";
import { getQuestionnaireLabel, type QuestionnaireId } from "@/utils/questionnaireMapping";

const Q = {
  pageBg: "var(--mantine-color-primary-0)",
  card: "#ffffff",
  brand: "var(--mantine-color-primary-6)",
  brandMuted: "var(--mantine-color-primary-7)",
  rowHover: "var(--mantine-color-primary-0)",
  rowSelected: "var(--mantine-color-primary-1)",
  rowBorder: "var(--mantine-color-primary-3)",
  divider: "var(--mantine-color-primary-1)",
  slateLine: "var(--mantine-color-slate-2)",
} as const;

const GRADE_CONFIG: Record<GradeCategory, { color: string; label: string; icon: React.ReactNode; tooltip: string }> = {
  alignment: {
    color: "#2563EB",
    label: "Alignment",
    icon: <IconCheck size={16} />,
    tooltip: "Areas of strong agreement between both parties",
  },
  discussion: {
    color: "#F59E0B",
    label: "Discussion",
    icon: <IconMessageCircle size={16} />,
    tooltip: "Topics requiring further conversation to align",
  },
  key_decision: {
    color: "#DC2626",
    label: "Key Decision",
    icon: <IconAlertTriangle size={16} />,
    tooltip: "Critical differences needing resolution",
  },
  clarification: {
    color: "#10B981",
    label: "Clarification",
    icon: <IconQuestionMark size={16} />,
    tooltip: "Areas lacking sufficient information",
  },
};

function getAnswerLabel(question: Question, answer: Answer | undefined): string {
  if (!answer) return "—";
  if (question.type === "multi_select") {
    const ids = (answer as MultiSelectAnswer).selectedOptionIds ?? [];
    if (ids.length === 0) return "—";
    return ids.map((id) => question.options.find((o) => o.id === id)?.label ?? id).join(", ");
  }
  const id = (answer as ScaleAnswer | PolicyAnswer).selectedOptionId;
  if (!id) return "—";
  return question.options.find((o) => o.id === id)?.label ?? id;
}

type Props = {
  questionnaire: Questionnaire;
  resolvedQuestionnaireId: QuestionnaireId;
  gradingResult: FullGradingResult | null;
  myAnswers: Record<string, Answer>;
  partnerAnswers: Record<string, Answer>;
};

export default function AssessmentResultsView({
  questionnaire,
  resolvedQuestionnaireId,
  gradingResult,
  myAnswers,
  partnerAnswers,
}: Props) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const sections = questionnaire.sections;
  const questionsById = useMemo(
    () => sections.flatMap((s) => s.questions).reduce((acc, q) => ({ ...acc, [q.id]: q }), {} as Record<string, Question>),
    [sections]
  );
  const resultsBySection = useMemo(
    () =>
      gradingResult?.results?.reduce(
        (acc, r) => {
          if (!acc[r.sectionId]) acc[r.sectionId] = [];
          acc[r.sectionId].push(r);
          return acc;
        },
        {} as Record<string, QuestionGradingResult[]>
      ),
    [gradingResult]
  );

  const summary = gradingResult?.summary;

  return (
    <Box>
      <Paper
        shadow="sm"
        radius="md"
        p={0}
        mb="lg"
        style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
      >
        <Box style={{ height: 10, background: Q.brand }} />
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap="md" p="lg">
          <Box style={{ flex: "1 1 240px" }}>
            <Text fw={700} fz="xl" c="primary.8" lh={1.25}>
              {getQuestionnaireLabel(resolvedQuestionnaireId)}
            </Text>
            {questionnaire.description && (
              <Text fz="sm" c="slate.6" mt={8} lh={1.5}>
                {questionnaire.description}
              </Text>
            )}
          </Box>
          <Badge
            variant="filled"
            color="yellow"
            size="lg"
            radius="sm"
            styles={{ root: { color: Q.brandMuted, fontWeight: 600 } }}
          >
            Both parties submitted
          </Badge>
        </Flex>
      </Paper>

      {gradingResult && summary ? (
        <>
          <Paper
            shadow="sm"
            radius="md"
            p="lg"
            mb="lg"
            style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}
          >
            <Text fw={700} fz="lg" c="primary.8" mb="md">
              Compatibility summary
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 2, md: 4 }} spacing="lg">
              {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map((cat) => {
                const config = GRADE_CONFIG[cat];
                const count = summary[cat];
                const pct = summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;
                return (
                  <Tooltip key={cat} label={config.tooltip} withArrow multiline w={200}>
                    <Paper shadow="xs" radius="md" p="md" style={{ background: Q.card, border: `1px solid ${Q.divider}` }}>
                      <Flex align="center" gap="md">
                        <RingProgress
                          size={90}
                          thickness={10}
                          roundCaps
                          sections={[{ value: pct, color: config.color }]}
                          label={
                            <Text fw={700} fz="sm" ta="center">
                              {pct}%
                            </Text>
                          }
                        />
                        <Box>
                          <Text fw={600} fz="sm">
                            {config.label}
                          </Text>
                          <Text fz="xs" c="slate.5">
                            {count} questions
                          </Text>
                        </Box>
                      </Flex>
                    </Paper>
                  </Tooltip>
                );
              })}
            </SimpleGrid>
          </Paper>

          {sections.map((section) => {
            const sectionResults = resultsBySection?.[section.id] ?? [];
            const isExpanded = expandedSections.has(section.id);
            const sectionSummary = sectionResults.reduce(
              (acc, r) => {
                acc[r.category] = (acc[r.category] || 0) + 1;
                return acc;
              },
              {} as Record<string, number>
            );
            return (
              <Paper
                key={section.id}
                shadow="sm"
                radius="md"
                mb="md"
                style={{ background: Q.card, border: `1px solid ${Q.slateLine}`, overflow: "hidden" }}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  p="md"
                  style={{ cursor: "pointer", background: Q.rowHover }}
                  onClick={() => toggleSection(section.id)}
                >
                  <Group gap="sm">
                    <Text fw={600} fz="md" c="primary.8">
                      {section.title}
                    </Text>
                    <Group gap={4}>
                      {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map((cat) => {
                        const count = sectionSummary[cat] || 0;
                        if (count === 0) return null;
                        return (
                          <Tooltip key={cat} label={`${GRADE_CONFIG[cat].label}: ${count}`}>
                            <Badge size="sm" variant="light" color={GRADE_CONFIG[cat].color}>
                              {count}
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  </Group>
                  {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                </Flex>
                <Collapse in={isExpanded}>
                  <Divider color={Q.divider} />
                  <Box p="md" style={{ background: Q.pageBg }}>
                    {sectionResults.map((result) => {
                      const config = GRADE_CONFIG[result.category];
                      const description = resolvedQuestionnaireId
                        ? getGradeDescription(resolvedQuestionnaireId, result.questionId, result.category)
                        : undefined;
                      return (
                        <Paper
                          key={result.questionId}
                          shadow="xs"
                          radius="md"
                          p="md"
                          mb="sm"
                          style={{ background: Q.card, border: `1px solid ${Q.slateLine}` }}
                        >
                          <Flex justify="space-between" align="flex-start" mb="xs">
                            <Text fz="sm" fw={500} style={{ flex: 1 }}>
                              {result.questionText}
                            </Text>
                            <Badge variant="light" color={config.color} size="sm" ml="sm" style={{ flexShrink: 0 }}>
                              {config.label}
                            </Badge>
                          </Flex>
                          {description && (
                            <Text fz="xs" c="slate.6" mb="xs">
                              {description}
                            </Text>
                          )}
                          {questionsById[result.questionId] && (
                            <Flex gap="xl" mt="xs" mb="xs" wrap="wrap">
                              <Box>
                                <Text fz="xs" fw={400} c="slate.8" mb={2}>
                                  Your Answer
                                </Text>
                                <Text fz="xs" c="slate.6">
                                  {getAnswerLabel(questionsById[result.questionId], myAnswers[result.questionId])}
                                </Text>
                              </Box>
                              <Box>
                                <Text fz="xs" fw={400} c="slate.8" mb={2}>
                                  Partner's Answer
                                </Text>
                                <Text fz="xs" c="slate.6">
                                  {getAnswerLabel(questionsById[result.questionId], partnerAnswers[result.questionId])}
                                </Text>
                              </Box>
                            </Flex>
                          )}
                          <Flex gap="xs">
                            <Badge variant="outline" size="xs" color="gray">
                              {result.questionType === "scale"
                                ? "Scale"
                                : result.questionType === "policy"
                                  ? "Single Select"
                                  : "Multi Select"}
                            </Badge>
                          </Flex>
                        </Paper>
                      );
                    })}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </>
      ) : (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <Loader size="md" color="primary" />
            <Text fz="sm" c="slate.6">
              Computing compatibility results...
            </Text>
          </Stack>
        </Center>
      )}
    </Box>
  );
}
